import React, { useRef, useEffect } from 'react';
import { FaEdit, FaTrash, FaUser, FaUserPlus, FaCalendarAlt, FaUserCheck, FaUserTimes } from 'react-icons/fa';

const StaffManagement = ({
  staff,
  newStaff,
  setNewStaff,
  editingStaff,
  setEditingStaff,
  departments,
  positions,
  handleAddStaff,
  handleEditStaff,
  handleDeleteStaff,
  generateEmployeeId,
  loading,
}) => {
  // Create ref for the form section
  const formRef = useRef(null);

  // Auto-scroll to form when editing starts
  useEffect(() => {
    if (editingStaff && formRef.current) {
      formRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [editingStaff]);

  // Enhanced edit handler with visual feedback
  const handleEditClick = (staffMember) => {
    setEditingStaff({ 
      ...staffMember, 
      bankName: staffMember.bankDetails?.bankName || '', 
      accountNumber: staffMember.bankDetails?.accountNumber || '', 
      accountName: staffMember.bankDetails?.accountName || '',
      pin: staffMember.pin || '',
      // Format dates for input fields
      dateOfEmployment: staffMember.dateOfEmployment ? 
        new Date(staffMember.dateOfEmployment).toISOString().split('T')[0] : '',
      employmentEndDate: staffMember.employmentEndDate ? 
        new Date(staffMember.employmentEndDate).toISOString().split('T')[0] : ''
    });
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-yellow-100 text-yellow-800';
      case 'terminated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // UPDATED: Check if position requires PIN (Cashier, Customer Service Assistant, Manager)
  const requiresPin = (position) => {
    return ['Cashier', 'Customer Service Assistant', 'Manager'].includes(position);
  };

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Staff Management</h2>
        <p className="text-sm text-gray-500">
          Device syncing and fingerprint enrollment now live under <span className="font-medium">Device Management</span>.
        </p>
      </div>

      {/* Add/Edit Staff Form */}
      <div 
        ref={formRef}
        className={`bg-white p-6 rounded-xl shadow-lg transition-all duration-300 ${
          editingStaff 
            ? 'ring-2 ring-blue-500 bg-blue-50/30' 
            : 'hover:shadow-xl'
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            {editingStaff ? (
              <>
                <FaEdit className="text-blue-600" />
                Edit Staff Member
                <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {editingStaff.firstName} {editingStaff.lastName}
                </span>
              </>
            ) : (
              <>
                <FaUserPlus className="text-green-600" />
                Add New Staff Member
              </>
            )}
          </h3>
          {editingStaff && (
            <button
              onClick={() => setEditingStaff(null)}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={editingStaff ? handleEditStaff : handleAddStaff} className="space-y-6">
          {/* Basic Information Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
              <FaUser className="text-gray-600" />
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee ID
                </label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.employeeId : newStaff.employeeId}
                  readOnly
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  placeholder="Auto-generated"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.firstName : newStaff.firstName}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, firstName: e.target.value }) 
                    : setNewStaff({ ...newStaff, firstName: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.lastName : newStaff.lastName}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, lastName: e.target.value }) 
                    : setNewStaff({ ...newStaff, lastName: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingStaff ? editingStaff.email : newStaff.email}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, email: e.target.value }) 
                    : setNewStaff({ ...newStaff, email: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={editingStaff ? editingStaff.phoneNumber : newStaff.phoneNumber}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, phoneNumber: e.target.value }) 
                    : setNewStaff({ ...newStaff, phoneNumber: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Department <span className="text-red-500">*</span></label>
                <select
                  value={editingStaff ? editingStaff.department : newStaff.department}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, department: e.target.value }) 
                    : setNewStaff({ ...newStaff, department: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position <span className="text-red-500">*</span></label>
                <select
                  value={editingStaff ? editingStaff.position : newStaff.position}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, position: e.target.value }) 
                    : setNewStaff({ ...newStaff, position: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Position</option>
                  {positions.map((pos) => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              
              {/* UPDATED: Show PIN field for Cashier, Customer Service Assistant, AND Manager */}
              {requiresPin(editingStaff ? editingStaff.position : newStaff.position) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN (4-6 digits) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingStaff ? editingStaff.pin : newStaff.pin}
                    onChange={(e) => editingStaff 
                      ? setEditingStaff({ ...editingStaff, pin: e.target.value }) 
                      : setNewStaff({ ...newStaff, pin: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter 4-6 digit PIN"
                    pattern="\d{4,6}"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required for kiosk login access (Cashier, Customer Service, Manager)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Employment Information Section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
              <FaCalendarAlt className="text-blue-600" />
              Employment Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editingStaff ? editingStaff.dateOfEmployment : newStaff.dateOfEmployment}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, dateOfEmployment: e.target.value }) 
                    : setNewStaff({ ...newStaff, dateOfEmployment: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={editingStaff ? editingStaff.status : newStaff.status || 'active'}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, status: e.target.value }) 
                    : setNewStaff({ ...newStaff, status: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              {(editingStaff ? editingStaff.status : newStaff.status) === 'terminated' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editingStaff ? editingStaff.employmentEndDate : newStaff.employmentEndDate}
                    onChange={(e) => editingStaff 
                      ? setEditingStaff({ ...editingStaff, employmentEndDate: e.target.value }) 
                      : setNewStaff({ ...newStaff, employmentEndDate: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min={editingStaff ? editingStaff.dateOfEmployment : newStaff.dateOfEmployment}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                <select
                  value={editingStaff ? editingStaff.employmentType : newStaff.employmentType}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, employmentType: e.target.value }) 
                    : setNewStaff({ ...newStaff, employmentType: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="full-time">Full-Time</option>
                  <option value="part-time">Part-Time</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Schedule</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.workSchedule : newStaff.workSchedule}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, workSchedule: e.target.value }) 
                    : setNewStaff({ ...newStaff, workSchedule: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Mon-Fri 9:00-17:00"
                />
              </div>
            </div>
          </div>

          {/* Device Settings Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Device Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RFID Card</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.card : newStaff.card}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, card: e.target.value }) 
                    : setNewStaff({ ...newStaff, card: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional RFID card number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Device Group</label>
                <input
                  type="number"
                  value={editingStaff ? editingStaff.group : newStaff.group}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, group: parseInt(e.target.value) || 1 }) 
                    : setNewStaff({ ...newStaff, group: parseInt(e.target.value) || 1 })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification Mode</label>
                <select
                  value={editingStaff ? editingStaff.verifyMode : newStaff.verifyMode}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, verifyMode: parseInt(e.target.value) }) 
                    : setNewStaff({ ...newStaff, verifyMode: parseInt(e.target.value) })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={0}>Any</option>
                  <option value={1}>Fingerprint</option>
                  <option value={2}>Face</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Bank Details Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Bank Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.bankName : newStaff.bankName}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, bankName: e.target.value }) 
                    : setNewStaff({ ...newStaff, bankName: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.accountNumber : newStaff.accountNumber}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, accountNumber: e.target.value }) 
                    : setNewStaff({ ...newStaff, accountNumber: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                <input
                  type="text"
                  value={editingStaff ? editingStaff.accountName : newStaff.accountName}
                  onChange={(e) => editingStaff 
                    ? setEditingStaff({ ...editingStaff, accountName: e.target.value }) 
                    : setNewStaff({ ...newStaff, accountName: e.target.value })
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-4 mt-8">
            <button
              type="submit"
              className={`px-6 py-3 text-white rounded-lg transition-colors flex items-center gap-2 ${
                editingStaff 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              disabled={loading}
            >
              {editingStaff ? <FaEdit /> : <FaUserPlus />}
              {editingStaff ? 'Update Staff' : 'Add Staff'}
            </button>
            {editingStaff && (
              <button
                type="button"
                onClick={() => setEditingStaff(null)}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Staff Directory - Completely Rewritten */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Staff Directory</h3>
        </div>
        
        {/* Mobile-First Card Layout */}
        <div className="block md:hidden">
          {staff.map((s) => (
            <div key={s._id} className="border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    s.status === 'active' ? 'bg-green-500' :
                    s.status === 'inactive' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}>
                    {s.firstName.charAt(0)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{s.firstName} {s.lastName}</div>
                    <div className="text-sm text-gray-500">{s.department} • {s.position}</div>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(s.status)}`}>
                      {s.status}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditClick(s)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <FaEdit size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this staff member?')) {
                        handleDeleteStaff(s._id);
                      }
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <FaTrash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Role</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Employment</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {staff.map((s) => (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="p-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        s.status === 'active' ? 'bg-green-500' :
                        s.status === 'inactive' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}>
                        {s.firstName.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{s.firstName} {s.lastName}</div>
                        <div className="text-sm text-gray-500">ID: {s.employeeId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{s.department}</div>
                      <div className="text-gray-500">{s.position}</div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="text-gray-400" size={10} />
                        <span>
                          {s.dateOfEmployment ? 
                            new Date(s.dateOfEmployment).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: '2-digit'}) 
                            : '-'
                          }
                        </span>
                      </div>
                      {s.employmentEndDate && (
                        <div className="flex items-center gap-1 mt-1 text-red-500">
                          <FaCalendarAlt className="text-red-400" size={10} />
                          <span>
                            {new Date(s.employmentEndDate).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: '2-digit'})}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(s.status)}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditClick(s)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this staff member?')) {
                            handleDeleteStaff(s._id);
                          }
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default StaffManagement;